import { type ReactElement, useCallback } from 'react';
import {
  useTable,
  List,
  DeleteButton,
  getDefaultSortOrder,
} from '@refinedev/antd';
import { useUpdate, type HttpError } from '@refinedev/core';
import { Table, Switch, Image, Tag, Space, Tooltip } from 'antd';

import type { AdminExample } from '@/types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExampleList(): ReactElement {
  const { tableProps, sorters } = useTable<AdminExample, HttpError>({
    resource: 'examples',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'sortOrder', order: 'asc' }],
    },
  });

  const { mutate: updateExample } = useUpdate<AdminExample>();

  const handleToggleActive = useCallback(
    (record: AdminExample): void => {
      updateExample({
        resource: 'examples',
        id: record.id,
        values: { isActive: !record.isActive },
      });
    },
    [updateExample]
  );

  return (
    <List>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1200 }} size="small">
        <Table.Column<AdminExample>
          title="Cover"
          dataIndex="coverPreviewUrl"
          render={(value: string | undefined) =>
            value ? (
              <Image
                src={value}
                alt="Cover"
                width={48}
                height={64}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                fallback="data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2248%22%20height%3D%2264%22%3E%3Crect%20fill%3D%22%23f0f0f0%22%20width%3D%2248%22%20height%3D%2264%22%2F%3E%3C%2Fsvg%3E"
              />
            ) : (
              <Tag>No cover</Tag>
            )
          }
          width={80}
          align="center"
        />

        <Table.Column<AdminExample>
          dataIndex="title"
          title="Title"
          sorter
          defaultSortOrder={getDefaultSortOrder('title', sorters)}
          ellipsis
          width={200}
        />

        <Table.Column<AdminExample>
          dataIndex="childName"
          title="Child Name"
          sorter
          defaultSortOrder={getDefaultSortOrder('childName', sorters)}
          width={120}
        />

        <Table.Column<AdminExample>
          dataIndex="childAge"
          title="Age"
          sorter
          defaultSortOrder={getDefaultSortOrder('childAge', sorters)}
          width={60}
          align="center"
        />

        <Table.Column<AdminExample>
          dataIndex="theme"
          title="Theme"
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
          width={120}
        />

        <Table.Column<AdminExample>
          dataIndex="isActive"
          title="Active"
          sorter
          defaultSortOrder={getDefaultSortOrder('isActive', sorters)}
          render={(_: boolean, record: AdminExample) => (
            <Switch
              checked={record.isActive}
              onChange={() => handleToggleActive(record)}
              size="small"
            />
          )}
          width={80}
          align="center"
        />

        <Table.Column<AdminExample>
          dataIndex="sortOrder"
          title="Sort Order"
          sorter
          defaultSortOrder={getDefaultSortOrder('sortOrder', sorters)}
          width={100}
          align="center"
        />

        <Table.Column<AdminExample>
          dataIndex="createdAt"
          title="Created"
          sorter
          defaultSortOrder={getDefaultSortOrder('createdAt', sorters)}
          render={(value: string) => formatDate(value)}
          width={160}
        />

        <Table.Column<AdminExample>
          title="Actions"
          fixed="right"
          width={80}
          render={(_: unknown, record: AdminExample) => (
            <Space size="small">
              <Tooltip title="Delete example">
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  resource="examples"
                />
              </Tooltip>
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
